import React from 'react';
import { newPurchaseOrderRequestKey, requestOutcome } from '@/data/purchaseOrderRecord';

export type PurchaseOrderRequestKeys = {
  /**
   * Sends one submission with its request key. The key is new unless the last attempt in this
   * `slot` sent this same payload (`fingerprint`) and its outcome is unknown (no answer): then the
   * retry reuses it, so it replays on the server instead of applying twice. A success or a refusal
   * ends the action and keeps nothing, so the next submission, even an identical one, is a new
   * action. Slots keep independent actions apart (one per line, one per kind of action).
   */
  run: <T>(fingerprint: string, send: (requestKey: string) => Promise<T>, slot?: string) => Promise<T>;
  /**
   * A new action starts (its dialog opened, its text changed): the key kept for a retry in `slot`
   * (every slot when none is named) is dropped.
   */
  reset: (slot?: string) => void;
};

/** Request keys for the Warehouse action RPCs (docs/warehouse.md): one per submitted payload. */
export default function usePurchaseOrderRequestKey(): PurchaseOrderRequestKeys {
  const kept = React.useRef(new Map<string, { fingerprint: string; key: string }>());
  return React.useMemo(() => ({
    async run<T>(fingerprint: string, send: (requestKey: string) => Promise<T>, slot = ''): Promise<T> {
      const retry = kept.current.get(slot);
      const key = retry && retry.fingerprint === fingerprint ? retry.key : newPurchaseOrderRequestKey();
      kept.current.set(slot, { fingerprint, key });
      const settle = (keep: boolean) => {
        if (!keep && kept.current.get(slot)?.key === key) kept.current.delete(slot);
      };
      try {
        const result = await send(key);
        settle(false);
        return result;
      } catch (error) {
        settle(requestOutcome(error) === 'unknown');
        throw error;
      }
    },
    reset(slot?: string) {
      if (slot === undefined) kept.current.clear();
      else kept.current.delete(slot);
    },
  }), []);
}
